import axios from 'axios';

/**
 * API base URL injected by Vite at BUILD TIME.
 * This MUST be defined in .env.production
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  throw new Error('VITE_API_BASE_URL is not defined');
}

// Export if other files need the raw base URL
export const API_URL = API_BASE_URL;

/**
 * Axios instance
 */
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

/**
 * Attach JWT token to every request
 */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Global auth error handling
 */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/* =========================
   AUTH API
========================= */
export const authAPI = {
  register: (data: {
    fullName: string;
    email: string;
    password: string;
    phone?: string;
    role?: string;
    adminSecret?: string;
  }) => api.post('/auth/register', data),

  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  getMe: () => api.get('/auth/profile'),
  logout: () => api.post('/auth/logout'),

  resetPassword: (
    email: string,
    password: string,
    confirmPassword: string,
    currentPassword?: string
  ) =>
    api.post('/auth/reset-password', {
      email,
      password,
      confirmPassword,
      currentPassword,
    }),

  updateProfile: (data: any) => api.put('/auth/profile', data),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPasswordWithToken: (token: string, password: string) =>
    api.post('/auth/reset-password-token', { token, password }),

  sendOtp: (phone: string) => api.post('/auth/send-otp', { phone }),
  verifyOtp: (phone: string, otp: string) =>
    api.post('/auth/verify-otp', { phone, otp }),
};

/* =========================
   PRODUCTS API
========================= */
export const productsAPI = {
  getAll: (params?: any) => api.get('/products', { params }),
  getOne: (id: string) => api.get(`/products/${id}`),
  create: (data: any) => api.post('/products', data),
  update: (id: string, data: any) => api.patch(`/products/${id}`, data),
  delete: (id: string) => api.delete(`/products/${id}`),
  getStats: () => api.get('/products/stats'),
};

/* =========================
   ORDERS API
========================= */
export const ordersAPI = {
  getAll: (params?: any) => api.get('/orders', { params }),
  getOne: (id: string) => api.get(`/orders/${id}`),
  create: (data: any) => api.post('/orders', data),
  updateStatus: (id: string, status: string, note?: string) =>
    api.patch(`/orders/${id}/status`, { status, note }),
  getMyDeliveries: () => api.get('/orders/my-deliveries'),
  getStats: () => api.get('/orders/stats'),
};

/* =========================
   CUSTOMERS API
========================= */
export const customersAPI = {
  getAll: (params?: any) => api.get('/customers', { params }),
  getOne: (id: string) => api.get(`/customers/${id}`),
  getStats: () => api.get('/customers/stats/summary'),
  getOrders: (id: string) => api.get(`/customers/${id}/orders`),
};

/* =========================
   ADDRESSES API
========================= */
export const addressesAPI = {
  getAll: () => api.get('/addresses'),
  getOne: (id: string) => api.get(`/addresses/${id}`),
  create: (data: any) => api.post('/addresses', data),
  update: (id: string, data: any) => api.patch(`/addresses/${id}`, data),
  delete: (id: string) => api.delete(`/addresses/${id}`),
  setDefault: (id: string) => api.patch(`/addresses/${id}/set-default`),
};

// Subscriptions API
export const subscriptionsAPI = {
  getAll: () => api.get('/subscriptions'),
  getOne: (id: string) => api.get(`/subscriptions/${id}`),
  create: (data: any) => api.post('/subscriptions', data),
  update: (id: string, data: any) => api.patch(`/subscriptions/${id}`, data),
  updateStatus: (id: string, status: string) =>
    api.patch(`/subscriptions/${id}/status`, { status }),
  skipNextDelivery: (id: string) =>
    api.post(`/subscriptions/${id}/skip-next`),
  getStats: () =>
    api.get('/subscriptions/stats/summary'),
  getUpcoming: () =>
    api.get('/subscriptions/upcoming'),
};

// Wallet API
export const walletAPI = {
  getBalance: () =>
    api.get('/wallet/balance'),

  addMoney: (amount: number, transactionId: string) =>
    api.post('/wallet/add-money', { amount, transactionId }),

  getTransactions: (limit = 50) =>
    api.get(`/wallet/transactions?limit=${limit}`),
};


// Stores API
export const storesAPI = {
  getAll: (params?: { organizationId?: string }) =>
    api.get('/stores', { params }),

  getMe: () =>
    api.get('/stores/me'),

  getOne: (id: string) =>
    api.get(`/stores/${id}`),

  create: (data: any) =>
    api.post('/stores', data),

  update: (id: string, data: any) =>
    api.patch(`/stores/${id}`, data),

  delete: (id: string) =>
    api.delete(`/stores/${id}`),
};

// Users API (Staff & Delivery Management)
export const usersAPI = {
  getAll: (params?: { role?: string; organizationId?: string; storeId?: string }) =>
    api.get('/users', { params }),

  getStaff: () =>
    api.get('/users/staff'),

  getOne: (id: string) =>
    api.get(`/users/${id}`),

  create: (data: any) =>
    api.post('/users', data),

  update: (id: string, data: any) =>
    api.patch(`/users/${id}`, data),

  delete: (id: string) =>
    api.delete(`/users/${id}`),

  invite: (data: {
    fullName: string;
    email: string;
    role: string;
    storeId?: string;
    organizationId?: string;
  }) =>
    api.post('/users/invite', data),

  acceptInvitation: (token: string, password: string) =>
    api.post('/users/accept-invitation', { token, password }),

  updateLocation: (lat: number, lng: number) =>
    api.patch('/users/location', { lat, lng }),

  submitDocuments: (
    id: string,
    documents: Array<{ type: string; url: string }>
  ) =>
    api.post(`/users/${id}/documents`, documents),

  verify: (
    id: string,
    status: string,
    documentType?: string,
    documentStatus?: string
  ) =>
    api.patch(`/users/${id}/verify`, {
      status,
      documentType,
      documentStatus,
    }),

  getNearbyDeliveryAgents: (
    lat: number,
    lng: number,
    radius?: number
  ) =>
    api.get('/users/nearby-delivery-agents', {
      params: { lat, lng, radius },
    }),
};

// Store Requests API
export const storeRequestsAPI = {
  create: (data: { storeId: string; message?: string }) =>
    api.post('/store-requests', data),

  getMyRequests: () =>
    api.get('/store-requests/my-requests'),

  getAll: (storeId?: string) =>
    api.get('/store-requests', {
      params: storeId ? { storeId } : undefined,
    }),

  updateStatus: (
    id: string,
    status: 'approved' | 'rejected',
    message?: string,
  ) =>
    api.patch(`/store-requests/${id}/status`, { status, message }),
};

// Tickets API (Help & Support)
export const ticketsAPI = {
  getAll: () =>
    api.get('/tickets'),

  getOne: (id: string) =>
    api.get(`/tickets/${id}`),

  create: (data: { subject: string; message: string }) =>
    api.post('/tickets', data),

  addMessage: (id: string, message: string) =>
    api.post(`/tickets/${id}/messages`, { message }),

  updateStatus: (id: string, status: string) =>
    api.patch(`/tickets/${id}/status`, { status }),
};

// Organizations API
export const organizationsAPI = {
  getAll: () =>
    api.get('/organizations'),

  getOne: (id: string) =>
    api.get(`/organizations/${id}`),

  create: (data: any) =>
    api.post('/organizations', data),

  update: (id: string, data: any) =>
    api.patch(`/organizations/${id}`, data),

  delete: (id: string) =>
    api.delete(`/organizations/${id}`),
};

/* =========================
   EXPORT DEFAULT
========================= */
export default api;